import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, ResourceRef, signal } from '@angular/core';
import { rxResource, takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import {
  EMAIL_DELIVERY_STATUSES,
  EMAIL_LOG_TYPES,
  EMAIL_MESSAGE_STATUSES,
  EmailDeliveryStatus,
  EmailLogListItem,
  EmailLogListParams,
  EmailLogListResponse,
  EmailLogState,
  EmailLogType,
  EmailMessageStatus,
  emailLogState,
  isEmailLogStateProblem,
} from '@models/email-logs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EmailLogsService } from '@services/email-logs.service';
import { showGenericErrorToast } from '@services/show-generic-error-toast';
import { ToastService } from '@services/toast.service';
import { assertUnreachable } from '@utils/assert-unreachable';
import { toDateOnly } from '@utils/date-only';
import { loadVisibleColumns, saveVisibleColumns } from '@utils/visible-columns-storage';
import { debounceTime, distinctUntilChanged, filter, map, skip } from 'rxjs';

// Column definition types

// `state` is the coalesced state column: one chip (`deliveryStatus ?? status`) with ONE dropdown over
// the union of both enums — pipeline-vs-delivery is an internal distinction the operator shouldn't have
// to know about; the selection maps to whichever API param the chosen value belongs to. The raw axes stay
// individually filterable via the advanced (hidden-by-default) status/deliveryStatus columns.
type FilterType = 'text' | 'number' | 'dropdown' | 'state' | 'daterange' | 'none';
type DropdownSource = 'logTypes' | 'statuses' | 'deliveryStatuses';
interface ColumnDef {
  key: string;
  labelKey: string;
  filterType: FilterType;
  /** Placeholder shown in the filter cell; defaults to the generic "Filter" text. */
  filterPlaceholderKey?: string;
  filterField?: string;
  filterFieldFrom?: string;
  filterFieldTo?: string;
  dropdownSource?: DropdownSource;
}

// Every column that can be shown, in display order. Each column's filter targets the API param that
// actually filters what the column DISPLAYS — notably `customer` searches the recipient snapshot
// (toName/toEmail), not the resolved customer name, so it lives on the recipient column while the
// customer column filters the exact `customerId`.
const ALL_COLUMNS: ColumnDef[] = [
  { key: 'subject', labelKey: 'email-logs.field.subject', filterType: 'text', filterField: 'subject' },
  {
    key: 'logType',
    labelKey: 'email-logs.field.logType',
    filterType: 'dropdown',
    filterField: 'logType',
    dropdownSource: 'logTypes',
  },
  { key: 'state', labelKey: 'email-logs.field.state', filterType: 'state' },
  {
    key: 'status',
    // `field.status` is the detail view's "Status" row; the grid needs to distinguish the two axes from
    // each other and from the coalesced `state` column, hence the more explicit label.
    labelKey: 'email-logs.field.pipelineStatus',
    filterType: 'dropdown',
    filterField: 'status',
    dropdownSource: 'statuses',
  },
  {
    key: 'deliveryStatus',
    labelKey: 'email-logs.field.deliveryStatus',
    filterType: 'dropdown',
    filterField: 'deliveryStatus',
    dropdownSource: 'deliveryStatuses',
  },
  {
    key: 'recipient',
    labelKey: 'email-logs.field.recipient',
    filterType: 'text',
    filterPlaceholderKey: 'email-logs.filter.recipient',
    filterField: 'customer',
  },
  {
    // Free-text like the old CRM: matches the recipient snapshot (toName/toEmail), which is the
    // customer's name/address at queue time — the API has no resolved-customer-name search. It shares
    // the `customer` param with the recipient column, so the two filter cells deliberately stay in sync.
    key: 'customerName',
    labelKey: 'email-logs.field.customer',
    filterType: 'text',
    filterPlaceholderKey: 'email-logs.filter.recipient',
    filterField: 'customer',
  },
  {
    key: 'customerId',
    labelKey: 'email-logs.field.customerId',
    filterType: 'number',
    filterPlaceholderKey: 'email-logs.filter.customer-id',
    filterField: 'customerId',
  },
  {
    key: 'reservationNumber',
    labelKey: 'email-logs.field.reservation',
    filterType: 'text',
    filterField: 'reservation',
  },
  {
    key: 'reservationId',
    labelKey: 'email-logs.field.reservationId',
    filterType: 'number',
    filterPlaceholderKey: 'email-logs.filter.reservation-id',
    filterField: 'reservationId',
  },
  { key: 'attemptCount', labelKey: 'email-logs.field.attemptCount', filterType: 'none' },
  { key: 'publicId', labelKey: 'email-logs.field.publicId', filterType: 'none' },
  {
    key: 'createdOn',
    labelKey: 'email-logs.field.createdOn',
    filterType: 'daterange',
    filterFieldFrom: 'createdFrom',
    filterFieldTo: 'createdTo',
  },
  {
    key: 'acceptedAt',
    labelKey: 'email-logs.field.acceptedAt',
    filterType: 'daterange',
    filterFieldFrom: 'acceptedFrom',
    filterFieldTo: 'acceptedTo',
  },
];

// There are too many columns to show them all now that the outbox exposes both state axes, the recipient
// snapshot and two timestamps; this is the operator's default view (the ticket's default field list plus
// the state chip). The rest stay one click away in the column picker (and in the `columns` query param) —
// notably `recipient` and `createdOn`, which together with their filter cells don't fit the table width.
const DEFAULT_VISIBLE_COLUMNS = ['subject', 'logType', 'state', 'customerName', 'reservationNumber', 'acceptedAt'];

// Bumped to `-v2` with the outbox rewrite: filters persisted by the previous build hold a NUMERIC
// logType, which the new API rejects with a 400. A new key throws those away instead of replaying them.
const FILTERS_SESSION_KEY = 'email-log-list-filters-v2';
// `-v3`: the v2 default column set (8 columns + paired filter cells laid out horizontally) overflowed the
// table width; stored v2 preferences would keep reproducing that layout.
const COLUMNS_STORAGE_KEY = 'email-log-list-columns-v3';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 25;

// Static label keys per enum value (rather than building the key from the value) so the i18n usage check
// can see them and a missing translation is detectable — see `translatedOrRaw`.
const LOG_TYPE_LABEL_KEYS: Record<EmailLogType, string> = {
  BookingRequest: 'email-logs.logType.BookingRequest',
  BookingConfirmation: 'email-logs.logType.BookingConfirmation',
  BookingCancellation: 'email-logs.logType.BookingCancellation',
  BookingVoucher: 'email-logs.logType.BookingVoucher',
  PaymentInvoice: 'email-logs.logType.PaymentInvoice',
  PaymentConfirmation: 'email-logs.logType.PaymentConfirmation',
  PaymentReminder: 'email-logs.logType.PaymentReminder',
  PaymentReminderInternal: 'email-logs.logType.PaymentReminderInternal',
  ReviewRequest: 'email-logs.logType.ReviewRequest',
  ServiceOrder: 'email-logs.logType.ServiceOrder',
  LoginCode: 'email-logs.logType.LoginCode',
  JoinUserGroup: 'email-logs.logType.JoinUserGroup',
  ContactAutoReply: 'email-logs.logType.ContactAutoReply',
  ContactInternal: 'email-logs.logType.ContactInternal',
  MediaSubmitted: 'email-logs.logType.MediaSubmitted',
  ExactNotification: 'email-logs.logType.ExactNotification',
  Legacy: 'email-logs.logType.Legacy',
};

const STATUS_LABEL_KEYS: Record<EmailMessageStatus, string> = {
  Pending: 'email-logs.status.Pending',
  Sending: 'email-logs.status.Sending',
  Accepted: 'email-logs.status.Accepted',
  Failed: 'email-logs.status.Failed',
  Cancelled: 'email-logs.status.Cancelled',
};

const DELIVERY_STATUS_LABEL_KEYS: Record<EmailDeliveryStatus, string> = {
  Sent: 'email-logs.deliveryStatus.Sent',
  Delivered: 'email-logs.deliveryStatus.Delivered',
  SoftBounced: 'email-logs.deliveryStatus.SoftBounced',
  HardBounced: 'email-logs.deliveryStatus.HardBounced',
  SpamComplaint: 'email-logs.deliveryStatus.SpamComplaint',
  Suppressed: 'email-logs.deliveryStatus.Suppressed',
  PolicyRejected: 'email-logs.deliveryStatus.PolicyRejected',
  Failed: 'email-logs.deliveryStatus.Failed',
  Unsubscribed: 'email-logs.deliveryStatus.Unsubscribed',
};

// Chip colours for the coalesced state. `Failed` is a member of both enums and is red either way, so one
// map over the union is enough.
const STATE_CLASSES: Record<EmailLogState, string> = {
  Pending: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100',
  Sending: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100',
  Accepted: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-100',
  Cancelled: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
  Sent: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100',
  Delivered: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100',
  Unsubscribed: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-100',
  SoftBounced: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100',
  HardBounced: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100',
  SpamComplaint: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100',
  Suppressed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100',
  PolicyRejected: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100',
  Failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100',
};

const NEUTRAL_CHIP_CLASSES = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-100';

// Options for the single state filter: the union of both enums, pipeline states first. `Failed` exists in
// both and is offered once — it filters the pipeline axis (dispatcher failures, the common case); the rare
// delivery-side `Failed` is reachable via the advanced deliveryStatus column.
const STATE_FILTER_OPTIONS: readonly EmailLogState[] = [
  ...EMAIL_MESSAGE_STATUSES,
  ...EMAIL_DELIVERY_STATUSES.filter((s) => !(EMAIL_MESSAGE_STATUSES as readonly string[]).includes(s)),
];

// Values a persisted dropdown filter is allowed to hold. Anything else is dropped on load so a stale or
// hand-edited sessionStorage entry can't turn into a 400 from enum model binding.
const DROPDOWN_ALLOWED_VALUES: Record<string, readonly string[]> = {
  logType: EMAIL_LOG_TYPES,
  status: EMAIL_MESSAGE_STATUSES,
  deliveryStatus: EMAIL_DELIVERY_STATUSES,
};

type RangeValue = string | null;

interface PersistedFilters {
  search: string;
  textFilters: Record<string, string>;
  dropdownFilters: Record<string, string>;
  rangeFilters: Record<string, RangeValue>;
  pageSize: number;
}

// Upper bound for id filters: the API binds them as Int32, so anything larger would fail model
// binding with a 400 instead of returning an empty result.
const MAX_ID_FILTER = 2_147_483_647;

/** Parses an id filter box; partial/invalid/out-of-range input is treated as "no filter" rather than sent as NaN. */
function toIdFilter(raw: string | undefined): number | null {
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed > 0 && parsed <= MAX_ID_FILTER ? parsed : null;
}

// What the grid falls back to when a request fails: the error state must not keep showing the previous
// filter set's rows, count and cursor (a live "next" holding a cursor from another query).
const EMPTY_LIST_RESPONSE: EmailLogListResponse = { items: [], totalCount: 0, pageSize: 0, nextCursor: null };

// Component

@Component({
  selector: 'app-email-log-list',
  templateUrl: './email-log-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatMenuModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatNativeDateModule,
    TranslatePipe,
  ],
})
export class EmailLogListComponent {
  private readonly emailLogsService = inject(EmailLogsService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);

  readonly allColumns = ALL_COLUMNS;
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;

  readonly dropdownOptions: Record<DropdownSource, readonly string[]> = {
    logTypes: EMAIL_LOG_TYPES,
    statuses: EMAIL_MESSAGE_STATUSES,
    deliveryStatuses: EMAIL_DELIVERY_STATUSES,
  };

  // Column visibility — synced to URL query param `columns` (AC: fields used are set in query parameter),
  // falls back to localStorage when there's no query param (e.g. a fresh visit).
  readonly visibleColumnKeys = signal<string[]>(
    loadVisibleColumns(
      COLUMNS_STORAGE_KEY,
      this.route.snapshot.queryParamMap.get('columns'),
      ALL_COLUMNS.map((c) => c.key),
      DEFAULT_VISIBLE_COLUMNS,
    ),
  );
  readonly displayedColumns = computed(() => this.visibleColumnKeys());
  readonly filterColumns = computed(() => this.displayedColumns().map((k) => `filter-${k}`));

  // Restore persisted filters for the session (AC: remember applied filters within the session).
  private readonly persisted = EmailLogListComponent.loadPersisted();

  readonly pageSize = signal(this.persisted.pageSize);

  // Keyset paging: `cursor` is the cursor that produced the page on screen (null = first page) and
  // `cursorStack` holds the cursors of the pages walked through to get here, so "previous" is a pop.
  // There is no page index by design — the API cannot jump to page N — but the walked depth gives us an
  // honest row range to display next to `totalCount`.
  private readonly cursor = signal<string | null>(null);
  private readonly cursorStack = signal<(string | null)[]>([]);

  readonly searchInput = signal(this.persisted.search);
  private readonly searchQuery = signal(this.persisted.search);

  private readonly textFilterInputs = signal<Record<string, string>>(this.persisted.textFilters);
  private readonly textFilters = signal<Record<string, string>>(this.persisted.textFilters);

  readonly dropdownFilters = signal<Record<string, string>>(this.persisted.dropdownFilters);
  readonly rangeFilters = signal<Record<string, RangeValue>>(this.persisted.rangeFilters);

  private readonly requestParams = computed<EmailLogListParams>(() => {
    const tf = this.textFilters();
    const df = this.dropdownFilters();
    const rf = this.rangeFilters();
    return {
      search: this.searchQuery() || null,
      subject: tf['subject'] || null,
      // Enum filters travel as the backend member name (e.g. `BookingConfirmation`), never a number.
      logType: (df['logType'] as EmailLogType | undefined) || null,
      status: (df['status'] as EmailMessageStatus | undefined) || null,
      deliveryStatus: (df['deliveryStatus'] as EmailDeliveryStatus | undefined) || null,
      customer: tf['customer'] || null,
      customerId: toIdFilter(tf['customerId']),
      reservation: tf['reservation'] || null,
      reservationId: toIdFilter(tf['reservationId']),
      createdFrom: rf['createdFrom'] || null,
      createdTo: rf['createdTo'] || null,
      acceptedFrom: rf['acceptedFrom'] || null,
      acceptedTo: rf['acceptedTo'] || null,
      cursor: this.cursor(),
      pageSize: this.pageSize(),
    };
  });

  readonly emailLogsResource: ResourceRef<EmailLogListResponse | undefined> = rxResource({
    params: () => this.requestParams(),
    stream: ({ params }) =>
      this.emailLogsService
        .getEmailLogs(params)
        .pipe(showGenericErrorToast(this.toastService, () => EMPTY_LIST_RESPONSE)),
  });

  private readonly response = toObservable(this.emailLogsResource.value).pipe(
    filter((v): v is EmailLogListResponse => v !== undefined),
  );

  readonly emailLogs = toSignal(this.response.pipe(map((v) => v.items)), { initialValue: [] as EmailLogListItem[] });
  readonly totalCount = toSignal(this.response.pipe(map((v) => v.totalCount)), { initialValue: 0 });
  /** Cursor for the page after the one on screen; null once we're on the last page. */
  private readonly nextCursor = toSignal(this.response.pipe(map((v) => v.nextCursor)), {
    initialValue: null as string | null,
  });

  // Row range of the page on screen. The server clamps pageSize to 1..100 and every option we offer is
  // inside that range, so the requested size is also the effective one.
  private readonly rowsBefore = computed(() => this.cursorStack().length * this.pageSize());
  readonly rangeStart = computed(() => (this.emailLogs().length === 0 ? 0 : this.rowsBefore() + 1));
  readonly rangeEnd = computed(() => this.rowsBefore() + this.emailLogs().length);

  readonly canGoPrevious = computed(() => this.cursorStack().length > 0 && !this.emailLogsResource.isLoading());
  readonly canGoNext = computed(() => this.nextCursor() !== null && !this.emailLogsResource.isLoading());

  constructor() {
    this.syncQueryParams();

    toObservable(this.searchInput)
      .pipe(skip(1), debounceTime(300), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe((v) => {
        this.searchQuery.set(v);
        this.resetPaging();
        this.persistFilters();
      });

    toObservable(this.textFilterInputs)
      .pipe(skip(1), debounceTime(300), takeUntilDestroyed())
      .subscribe((v) => {
        this.textFilters.set(v);
        this.resetPaging();
        this.persistFilters();
      });
  }

  // Event handlers

  onSearchInput(value: string): void {
    this.searchInput.set(value);
  }

  onTextFilterInput(filterField: string, value: string): void {
    this.textFilterInputs.update((prev) => ({ ...prev, [filterField]: value }));
  }

  onDropdownFilterChange(filterField: string, value: string): void {
    this.dropdownFilters.update((prev) => ({ ...prev, [filterField]: value }));
    this.resetPaging();
    this.persistFilters();
  }

  readonly stateFilterOptions = STATE_FILTER_OPTIONS;

  /** The state cell shows whichever axis is filtered; with both set (via advanced columns) the pipeline one. */
  getStateFilterValue(): string {
    return this.dropdownFilters()['status'] || this.dropdownFilters()['deliveryStatus'] || '';
  }

  /**
   * One dropdown, two API params: the chosen value is written to the axis it belongs to and the other
   * axis is cleared, so the operator never has to know which enum a state comes from.
   */
  onStateFilterChange(value: string): void {
    const isPipeline = (EMAIL_MESSAGE_STATUSES as readonly string[]).includes(value);
    this.dropdownFilters.update((prev) => ({
      ...prev,
      status: isPipeline ? value : '',
      deliveryStatus: isPipeline ? '' : value,
    }));
    this.resetPaging();
    this.persistFilters();
  }

  stateOptionLabel(value: EmailLogState): string {
    return (EMAIL_MESSAGE_STATUSES as readonly string[]).includes(value)
      ? this.statusLabel(value as EmailMessageStatus)
      : this.deliveryStatusLabel(value as EmailDeliveryStatus);
  }

  // Date filters use the Material datepicker (Date in, ISO yyyy-MM-dd string stored for the query param).
  onDateFilterChange(field: string, date: Date | null): void {
    const iso = toDateOnly(date);
    this.rangeFilters.update((prev) => ({ ...prev, [field]: iso }));
    this.resetPaging();
    this.persistFilters();
  }

  /** Clears both ends of a range column's date filter in one update. */
  clearDateRange(col: ColumnDef): void {
    this.rangeFilters.update((prev) => ({ ...prev, [col.filterFieldFrom!]: null, [col.filterFieldTo!]: null }));
    this.resetPaging();
    this.persistFilters();
  }

  getDateFilterValue(field: string): Date | null {
    const v = this.rangeFilters()[field];
    if (typeof v !== 'string' || !v) return null;
    // Parse the yyyy-MM-dd string as a LOCAL date; new Date('yyyy-MM-dd') would parse as UTC and
    // can shift the day in non-UTC timezones.
    const [y, m, d] = v.split('-').map(Number);
    return new Date(y, m - 1, d);
  }

  clearAllFilters(): void {
    this.searchInput.set('');
    this.searchQuery.set('');
    this.textFilterInputs.set({});
    this.textFilters.set({});
    this.dropdownFilters.set({});
    this.rangeFilters.set({});
    this.resetPaging();
    this.persistFilters();
  }

  readonly hasActiveFilters = computed(
    () =>
      !!this.searchInput() ||
      Object.values(this.textFilterInputs()).some((v) => !!v) ||
      Object.values(this.dropdownFilters()).some((v) => !!v) ||
      Object.values(this.rangeFilters()).some((v) => v != null),
  );

  // Paging

  onPageSizeChange(size: number): void {
    this.pageSize.set(size);
    // A different page size invalidates the walked cursors: they point at row boundaries of the old size.
    this.resetPaging();
    this.persistFilters();
  }

  goToNextPage(): void {
    const next = this.nextCursor();
    // `nextCursor` still holds the previous response's value until the new page lands, so a second click
    // before then would push the cursor we already moved to and desync the walked depth. A real response
    // never repeats the cursor it was fetched with (that would mean an empty page, which has no cursor).
    if (!next || next === this.cursor()) return;
    this.cursorStack.update((stack) => [...stack, this.cursor()]);
    this.cursor.set(next);
  }

  goToPreviousPage(): void {
    const stack = this.cursorStack();
    if (stack.length === 0) return;
    this.cursor.set(stack[stack.length - 1]);
    this.cursorStack.set(stack.slice(0, -1));
  }

  /** Back to the first page. Called for every filter change — a cursor only means anything for one filter set. */
  private resetPaging(): void {
    this.cursor.set(null);
    this.cursorStack.set([]);
  }

  // Column helpers

  toggleColumn(key: string): void {
    const current = this.visibleColumnKeys();
    const updated = current.includes(key) ? current.filter((k) => k !== key) : [...current, key];
    if (updated.length === 0) return;
    const ordered = ALL_COLUMNS.map((c) => c.key).filter((k) => updated.includes(k));
    this.visibleColumnKeys.set(ordered);
    saveVisibleColumns(COLUMNS_STORAGE_KEY, ordered);
    this.syncQueryParams();
  }

  private syncQueryParams(): void {
    this.router.navigate([], {
      queryParams: { columns: this.visibleColumnKeys().join(',') },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  isColumnVisible(key: string): boolean {
    return this.visibleColumnKeys().includes(key);
  }

  getTextFilterValue(filterField: string): string {
    return this.textFilterInputs()[filterField] ?? '';
  }

  getDropdownFilterValue(filterField: string): string {
    return this.dropdownFilters()[filterField] ?? '';
  }

  // Display helpers

  optionLabel(source: DropdownSource, value: string): string {
    switch (source) {
      case 'logTypes':
        return this.logTypeLabel(value as EmailLogType);
      case 'statuses':
        return this.statusLabel(value as EmailMessageStatus);
      case 'deliveryStatuses':
        return this.deliveryStatusLabel(value as EmailDeliveryStatus);
      default:
        return assertUnreachable(source);
    }
  }

  logTypeLabel(value: EmailLogType): string {
    return this.translatedOrRaw(LOG_TYPE_LABEL_KEYS[value], value);
  }

  statusLabel(value: EmailMessageStatus): string {
    return this.translatedOrRaw(STATUS_LABEL_KEYS[value], value);
  }

  deliveryStatusLabel(value: EmailDeliveryStatus | null): string {
    if (value === null) return this.translate.instant('email-logs.deliveryStatus.none');
    return this.translatedOrRaw(DELIVERY_STATUS_LABEL_KEYS[value], value);
  }

  /** The single chip in the state column: the delivery outcome once reported, otherwise the pipeline state. */
  stateLabel(row: EmailLogListItem): string {
    return row.deliveryStatus === null ? this.statusLabel(row.status) : this.deliveryStatusLabel(row.deliveryStatus);
  }

  stateClass(row: EmailLogListItem): string {
    // Widened on purpose: the backend enums may grow, and an unknown state should get the neutral chip
    // instead of an undefined class list.
    const classes: Record<string, string | undefined> = STATE_CLASSES;
    return classes[emailLogState(row)] ?? NEUTRAL_CHIP_CLASSES;
  }

  /** Both axes spelled out, since the chip only shows the coalesced one. */
  stateTooltip(row: EmailLogListItem): string {
    const status = `${this.translate.instant('email-logs.field.pipelineStatus')}: ${this.statusLabel(row.status)}`;
    const delivery = `${this.translate.instant('email-logs.field.deliveryStatus')}: ${this.deliveryStatusLabel(row.deliveryStatus)}`;
    return `${status} · ${delivery}`;
  }

  isProblemRow(row: EmailLogListItem): boolean {
    return isEmailLogStateProblem(emailLogState(row));
  }

  /**
   * ngx-translate echoes the key back when there is no translation for it; degrade to the raw enum name
   * so a backend that grew a new log type / status still reads sensibly instead of showing an i18n key.
   */
  private translatedOrRaw(key: string | undefined, raw: string): string {
    if (!key) return raw;
    const translated: string = this.translate.instant(key);
    return translated === key ? raw : translated;
  }

  // Session persistence

  private persistFilters(): void {
    const payload: PersistedFilters = {
      search: this.searchInput(),
      textFilters: this.textFilterInputs(),
      dropdownFilters: this.dropdownFilters(),
      rangeFilters: this.rangeFilters(),
      pageSize: this.pageSize(),
    };
    try {
      sessionStorage.setItem(FILTERS_SESSION_KEY, JSON.stringify(payload));
    } catch (e) {
      console.warn("sessionStorage unavailable (e.g. private mode) — filters simply won't persist.", e);
    }
  }

  private static loadPersisted(): PersistedFilters {
    const empty: PersistedFilters = {
      search: '',
      textFilters: {},
      dropdownFilters: {},
      rangeFilters: {},
      pageSize: DEFAULT_PAGE_SIZE,
    };
    try {
      const raw = sessionStorage.getItem(FILTERS_SESSION_KEY);
      if (!raw) return empty;
      const parsed = { ...empty, ...(JSON.parse(raw) as Partial<PersistedFilters>) };
      return {
        ...parsed,
        dropdownFilters: EmailLogListComponent.sanitizeDropdownFilters(parsed.dropdownFilters),
        pageSize: PAGE_SIZE_OPTIONS.includes(parsed.pageSize as (typeof PAGE_SIZE_OPTIONS)[number])
          ? parsed.pageSize
          : DEFAULT_PAGE_SIZE,
      };
    } catch {
      return empty;
    }
  }

  /** Keeps only enum values the API still knows — a bogus one would come back as a 400, not as no filter. */
  private static sanitizeDropdownFilters(filters: Record<string, string> | undefined): Record<string, string> {
    if (!filters) return {};
    return Object.fromEntries(
      Object.entries(filters).filter(([field, value]) => !value || DROPDOWN_ALLOWED_VALUES[field]?.includes(value)),
    );
  }

  // Row actions

  navigateToEmailLog(id: number): void {
    this.router.navigate(['/email-logs', id], { queryParams: { backUrl: this.router.url } });
  }
}
